import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Paper,
  TextInput,
  Table,
  Pagination,
  Loader,
  Center,
  Divider,
  Select,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconFilter,
  IconRefresh,
  IconShieldCheck,
} from "@tabler/icons-react";
import {
  useStainsAdmin,
  useCreateStain,
  useUpdateStain,
  useToggleStainActive,
} from "../../../hooks/admin/useStains";
import { useCptCodesAdmin } from "../../../hooks/admin/useCptCodes";
import { useHasPermission } from "../../../hooks/useHasPermission";
import StainForm, {
  StainFormValues,
} from "../../../components/admin/stains/StainForm";
import StainFilterModal from "../../../components/admin/stains/StainFilterModal";
import StainActiveFilters from "../../../components/admin/stains/StainActiveFilters";
import StainMarkerVerificationModal from "../../../components/admin/stains/StainMarkerVerificationModal";
import { StainAdminRow } from "../../../components/admin/stains/StainAdminRow";
import {
  Stain,
  StainFilters,
  StainSearchParams,
} from "../../../services/admin/stains.service";

export default function StainsAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    verificationModalOpened,
    { open: openVerificationModal, close: closeVerificationModal },
  ] = useDisclosure(false);

  // Page state
  const [pageSize, setPageSize] = useState<string>("20");
  const pageSizeNumber = useMemo(
    () => (pageSize === "all" ? Infinity : parseInt(pageSize, 10)),
    [pageSize]
  );
  const [editingStain, setEditingStain] = useState<Stain | null>(null);
  const [stainToToggle, setStainToToggle] = useState<Stain | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<StainFilters>({});
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Build search params for the API
  const searchParams: StainSearchParams = useMemo(() => {
    const params: StainSearchParams = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.stainTypes?.length) params.stainTypes = filters.stainTypes;
    if (filters.availability && filters.availability !== "all")
      params.availability = filters.availability;
    if (filters.status && filters.status !== "all")
      params.status = filters.status;
    if (filters.cptCodeIds?.length) params.cptCodeIds = filters.cptCodeIds;
    if (filters.showWithoutCpt) params.showWithoutCpt = filters.showWithoutCpt;
    return params;
  }, [debouncedSearch, filters]);

  // Data hooks - pass search params for server-side filtering
  const {
    data: stains = [],
    isLoading,
    refetch,
  } = useStainsAdmin(searchParams);
  const { data: cptCodes = [] } = useCptCodesAdmin();
  const createStain = useCreateStain();
  const updateStain = useUpdateStain();
  const toggleStainActive = useToggleStainActive();
  const canRunVerification = useHasPermission("stains.marker_verification.run");

  // Data is already filtered from the server
  const filteredStains = stains;

  // Pagination
  const totalPages = useMemo(
    () =>
      pageSizeNumber === Infinity
        ? 1
        : Math.ceil(filteredStains.length / pageSizeNumber),
    [filteredStains.length, pageSizeNumber]
  );
  const paginatedStains = useMemo(() => {
    if (pageSizeNumber === Infinity) return filteredStains;
    const start = (page - 1) * pageSizeNumber;
    return filteredStains.slice(start, start + pageSizeNumber);
  }, [filteredStains, page, pageSizeNumber]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.stainTypes && filters.stainTypes.length > 0) ||
    filters.availability ||
    filters.status ||
    (filters.cptCodeIds && filters.cptCodeIds.length > 0) ||
    filters.showWithoutCpt;

  // Handlers
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(1);
    },
    []
  );

  const handlePageSizeChange = useCallback((val: string | null) => {
    setPageSize(val || "20");
    setPage(1);
  }, []);

  const handleAdd = useCallback(() => {
    setEditingStain(null);
    openFormModal();
  }, [openFormModal]);

  const handleEdit = useCallback(
    (stain: Stain) => {
      setEditingStain(stain);
      openFormModal();
    },
    [openFormModal]
  );

  const handleSubmit = useCallback(
    async (values: StainFormValues) => {
      // Convert empty strings to null for optional UUID fields
      // null is explicitly sent to the backend to clear the field
      // undefined would be stripped from JSON and the field would remain unchanged
      const sanitizedValues = {
        ...values,
        cptId: values.cptId || null,
        defaultProviderId: values.defaultProviderId || null,
        leicaMarker: values.leicaMarker || null,
        biocareMarker: values.biocareMarker || null,
      };

      if (editingStain) {
        await updateStain.mutateAsync({
          id: editingStain.id,
          ...sanitizedValues,
        });
      } else {
        await createStain.mutateAsync(sanitizedValues);
      }
      closeFormModal();
    },
    [editingStain, updateStain, createStain, closeFormModal]
  );

  const handleToggleActive = useCallback(
    (stain: Stain) => {
      setStainToToggle(stain);
      openConfirmModal();
    },
    [openConfirmModal]
  );

  const confirmToggleActive = useCallback(async () => {
    if (stainToToggle) {
      await toggleStainActive.mutateAsync({
        id: stainToToggle.id,
        isActive: !stainToToggle.isActive,
      });
      closeConfirmModal();
      setStainToToggle(null);
    }
  }, [stainToToggle, toggleStainActive, closeConfirmModal]);

  const handleFiltersApply = useCallback((newFilters: StainFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const toggleRowExpanded = useCallback((stainId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(stainId)) {
        next.delete(stainId);
      } else {
        next.add(stainId);
      }
      return next;
    });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Stains</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage stains for specimen processing
              </Text>
            </div>
            <Group>
              {canRunVerification && (
                <Button
                  leftSection={<IconShieldCheck size={16} />}
                  onClick={openVerificationModal}
                  variant="light"
                  color="blue"
                >
                  Verify Markers
                </Button>
              )}
              <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
                Add New
              </Button>
            </Group>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by code, description, or marker..."
                  value={search}
                  onChange={handleSearchChange}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={handleRefetch}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button variant="light" onClick={openFilterModal} fullWidth>
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <StainActiveFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    onClearAll={handleClearAllFilters}
                    cptCodes={cptCodes}
                  />
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredStains.length} of {stains.length} stains
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Stains ({filteredStains.length})
                </Text>
                <Group gap="lg">
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">
                      Rows per page:
                    </Text>
                    <Select
                      size="xs"
                      w={80}
                      value={pageSize}
                      data={[
                        { value: "20", label: "20" },
                        { value: "50", label: "50" },
                        { value: "100", label: "100" },
                        { value: "all", label: "All" },
                      ]}
                      onChange={handlePageSizeChange}
                    />
                  </Group>

                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing{" "}
                      {filteredStains.length === 0
                        ? 0
                        : pageSizeNumber === Infinity
                        ? 1
                        : (page - 1) * pageSizeNumber + 1}
                      -
                      {pageSizeNumber === Infinity
                        ? filteredStains.length
                        : Math.min(
                            page * pageSizeNumber,
                            filteredStains.length
                          )}{" "}
                      of {filteredStains.length}
                    </Text>
                    {totalPages > 1 && (
                      <Pagination
                        total={totalPages}
                        value={page}
                        onChange={handlePageChange}
                        size="sm"
                      />
                    )}
                  </Group>
                </Group>
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredStains.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No stains match the current filters"
                    : "No stains found"}
                </Text>
              ) : (
                <Table.ScrollContainer minWidth={1000} h={600}>
                  <Table
                    striped
                    highlightOnHover
                    stickyHeader
                    stickyHeaderOffset={0}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 40 }} />
                        <Table.Th>Stain</Table.Th>
                        <Table.Th>Markers</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Availability</Table.Th>
                        <Table.Th>CPT Code</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedStains.map((stain) => (
                        <StainAdminRow
                          key={stain.id}
                          stain={stain}
                          isExpanded={expandedRows.has(stain.id)}
                          onToggleExpand={toggleRowExpanded}
                          onEdit={handleEdit}
                          onToggleActive={handleToggleActive}
                        />
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Stain Modal */}
        <Modal.Root
          opened={formModalOpened}
          onClose={closeFormModal}
          closeOnClickOutside={false}
          size="xl"
          centered
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingStain ? "Edit Stain" : "Add New Stain"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <StainForm
                initialValues={editingStain || undefined}
                onSubmit={handleSubmit}
              />
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="stain-form"
                loading={createStain.isPending || updateStain.isPending}
              >
                {editingStain ? "Update" : "Create"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Filter Modal */}
        <StainFilterModal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          filters={filters}
          onApply={handleFiltersApply}
          cptCodes={cptCodes}
        />

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal.Root
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          size="sm"
          centered
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {stainToToggle?.isActive
                  ? "Deactivate Stain"
                  : "Activate Stain"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body>
              <Stack gap="md">
                <Text>
                  Are you sure you want to{" "}
                  {stainToToggle?.isActive ? "deactivate" : "activate"} the
                  stain <strong>{stainToToggle?.code}</strong>?
                </Text>
                {stainToToggle?.isActive && (
                  <Text size="sm" c="dimmed">
                    Deactivated stains will no longer be available for
                    selection.
                  </Text>
                )}
              </Stack>
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={stainToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleStainActive.isPending}
              >
                {stainToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>
        <StainMarkerVerificationModal
          opened={verificationModalOpened}
          onClose={closeVerificationModal}
        />
      </Box>
    </Container>
  );
}
